import { api as getAvailableFrameworksApi } from '@main/graphql/queries/GetAvailableFrameworksForOrganization.generated';
import { api as getProgramsApi } from '@main/graphql/queries/GetPrograms.generated';
import { api as getOrganizationsSubscriptionApi } from '@main/graphql/subscriptions/GetOrganizationsSubscription';
import { createSorter } from '@main/shared/utils';
import { createSelector } from '@reduxjs/toolkit';

import { BackofficeRootState } from '../../store';

const sorter = createSorter();

// selectors
const appState = (state: BackofficeRootState) => state;

const getOrganizations = createSelector(
  [appState],
  (state) =>
    getOrganizationsSubscriptionApi.endpoints.GetOrganizations.select()(state).data
      ?.organizations || [],
);

export const getOrganizationsMap = createSelector([getOrganizations], (orgs) => {
  const orgsWithSubOrgs = orgs.map((org) => ({
    ...org,
    subOrgs: [] as (typeof org)[],
  }));

  const orgsMap = orgsWithSubOrgs.reduce<Record<string, (typeof orgsWithSubOrgs)[number]>>(
    (acc, org) => {
      acc[org.id] = org;

      return acc;
    },
    {},
  );

  orgsWithSubOrgs.forEach((org) => {
    if (org.parent_organization_id) {
      orgsMap[org.parent_organization_id]?.subOrgs.push(org);
    }
  });

  return orgsMap;
});

const getProgramsApiData = createSelector(
  [appState, (_, orgId: string) => orgId],
  (state, orgId) => {
    return (
      getProgramsApi.endpoints.GetPrograms.select({
        orgId,
      })(state).data?.programs || []
    );
  },
);

export const getProgramsList = createSelector([getProgramsApiData], (programs) => {
  const mappedPrograms = programs
    .map((program) => ({
      ...program,
      framework: program.framework || program.template_framework,
    }))
    .sort(sorter);

  return mappedPrograms;
});

export type Program = ReturnType<typeof getProgramsList>[number];

const FRAMEWORK_TYPE = {
  custom: 'Custom',
  template: 'Template',
} as const;

const getAvailableFrameworksApiData = createSelector(
  [appState, (_, orgId: string) => orgId],
  (state, orgId) => {
    return getAvailableFrameworksApi.endpoints.GetAvailableFrameworksForOrganization.select({
      orgId,
    })(state).data;
  },
);

export const getOrgFrameworks = createSelector([getAvailableFrameworksApiData], (data) => {
  const custom =
    data?.customFrameworks.map((framework) => ({
      id: framework.id,
      name: framework.name,
      type: FRAMEWORK_TYPE.custom,
    })) ?? [];
  const template =
    data?.templateFrameworks.map((framework) => ({
      id: framework.id,
      name: framework.name,
      type: FRAMEWORK_TYPE.template,
    })) ?? [];

  return [...custom, ...template];
});
